import React, { useEffect, useRef, useState, useCallback } from 'react';

import { MaximizeIcon, PlusIcon, MinusIcon, LocationNavigationIcon } from '@vlabs/icons';
import { ButtonScrimGroup } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import cn from 'classnames';
import type { Map as TMap } from 'ol';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import Draw, { createBox } from 'ol/interaction/Draw';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import { fromLonLat, toLonLat } from 'ol/proj';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import View from 'ol/View';
import { createPortal } from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

// @ts-expect-error не подгружается декларация
import st from './MapWrapper.module.sass';

const defaultCenter = [4187399, 7508881];
const defaultZoom = 10;

interface MapWrapperProps {
  prefixId?: string;
  prefixName?: string;
  isFullScreenEnabled?: boolean;
}

type TAction = 'increase' | 'decrease' | 'geolocation';
// FIXME требуется рефакторинг
// https://jira.visionlabs.ru/browse/GUI-3068
export function MapWrapper({ prefixId = '', prefixName = '', isFullScreenEnabled = false }: MapWrapperProps) {
  const [isFullScreen, setFullScreen] = useState(false);

  const { t } = useTranslation();
  const {
    setValue,
    clearErrors,
    getValues,
  } = useFormContext();

  const mapTargetElement = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<TMap | null>(null);

  const toggleFullScreen = () => {
    setFullScreen((p) => !p);
  };

  const handleGetUserLocation = useCallback(() => {
    return new Promise<GeolocationPosition | null>((resolve) => {
      if (!navigator.geolocation) {
        toast.error(t('maps:location_error.unsupported'));
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos),
        (error) => {
          console.warn('Geolocation error:', error);
          toast.error(t('maps:location_error.unavailable_location'));
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 5000 },
      );
    });
  }, [t]);

  const handleChangeMapView = async (action: TAction) => {
    if (!mapRef.current) return;

    const currentView = mapRef.current.getView();
    const currentCenter = currentView.getCenter();
    const currentZoom = currentView.getZoom();

    if (!currentCenter || !currentZoom) return;

    if (action === 'increase') currentView.setZoom(currentZoom + 1);
    if (action === 'decrease') currentView.setZoom(currentZoom - 1);
    if (action === 'geolocation') {
      const location = await handleGetUserLocation();
      if (location) {
        const { latitude, longitude } = location.coords;
        currentView.setCenter(fromLonLat([longitude, latitude]));
        currentView.setZoom(14);
      } else {
        currentView.setCenter(defaultCenter);
        currentView.setZoom(defaultZoom);
      }
    }
  };

  const source = new VectorSource({ wrapX: false });
  const withPrefixName = useCallback((value) => [prefixName, value].filter(Boolean).join('.'), [prefixName]);
  const withPrefixId = useCallback((value) => [prefixId, value].filter(Boolean).join('.'), [prefixId]);

  useEffect(() => {
    if (!mapRef.current) return;
    if (!isFullScreen && mapTargetElement.current) {
      mapRef.current.setTarget(mapTargetElement.current);
      return;
    }

    const target = document.getElementById(withPrefixId('map'));
    if (!target) return;

    mapRef.current.setTarget(target);

    requestAnimationFrame(() => {
      mapRef.current?.updateSize();
      mapRef.current?.render();
    });
  }, [isFullScreen, withPrefixId]);

  const draw = new Draw({
    source,
    type: 'Circle',
    geometryFunction: createBox(),
  });

  useEffect(() => {
    if (mapRef.current) return undefined;

    const raster = new TileLayer({ source: new OSM() });
    const vector = new VectorLayer({ source });

    const $map = new Map({
      target: mapTargetElement.current || '',
      controls: [],
      layers: [raster, vector],
      view: new View({
        center: defaultCenter,
        zoom: defaultZoom,
      }),
    });
    mapRef.current = $map;
    $map.addInteraction(draw);

    const origin_latitude = getValues('origin_latitude');
    const origin_longitude = getValues('origin_longitude');
    const latitude_delta = getValues('latitude_delta');
    const longitude_delta = getValues('longitude_delta');

    const isNumber = (v: unknown) => typeof v === 'number' && !Number.isNaN(v);
    const hasValidValues = [origin_latitude, origin_longitude, latitude_delta, longitude_delta].every(isNumber);

    if (hasValidValues) {
      const lat1 = origin_latitude - latitude_delta;
      const lat2 = origin_latitude + latitude_delta;
      const lon1 = origin_longitude - longitude_delta;
      const lon2 = origin_longitude + longitude_delta;

      const coords = [
        fromLonLat([lon1, lat1]),
        fromLonLat([lon1, lat2]),
        fromLonLat([lon2, lat2]),
        fromLonLat([lon2, lat1]),
        fromLonLat([lon1, lat1]),
      ];

      const polygon = new Polygon([coords]);
      const feature = new Feature(polygon);
      source.addFeature(feature);
    }

    draw.on('drawstart', () => {
      source.clear();
    });

    source.on('addfeature', (e) => {
      const LATITUDE_INDEX = 1;
      const LONGITUDE_INDEX = 0;

      const { feature } = e;
      // @ts-expect-error у прототипа есть этот метод
      const coordsList = feature?.getGeometry()?.getCoordinates()[0];
      const lon1 = toLonLat(coordsList[0])[LONGITUDE_INDEX];
      const lat1 = toLonLat(coordsList[0])[LATITUDE_INDEX];
      const lon2 = toLonLat(coordsList[2])[LONGITUDE_INDEX];
      const lat2 = toLonLat(coordsList[2])[LATITUDE_INDEX];

      const lngDelta = (lon2 - lon1) / 2;
      const latDelta = (lat2 - lat1) / 2;

      const centerLon = lon1 + lngDelta;
      const centerLat = lat1 + latDelta;

      setValue(withPrefixName('origin_latitude'), centerLat);
      setValue(withPrefixName('origin_longitude'), centerLon);
      setValue(withPrefixName('latitude_delta'), latDelta);
      setValue(withPrefixName('longitude_delta'), lngDelta);

      clearErrors([
        withPrefixName('origin_latitude'),
        withPrefixName('origin_longitude'),
        withPrefixName('latitude_delta'),
        withPrefixName('longitude_delta'),
      ]);
    });

    return () => $map.setTarget('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mapContent = (
    <>
      {!isFullScreen && <div className={st.Title}>{t('select_zone_on_map')}</div>}
      <div className={cn(st.Wrapper, { [st.FullScreen]: isFullScreen })}>
        {isFullScreenEnabled && (
          <ButtonScrimGroup className={cn(st.Button, st.MaximizeButton)} orientation="vertical">
            <IconButton icon={<MaximizeIcon />} onClick={toggleFullScreen} />
          </ButtonScrimGroup>
        )}
        <ButtonScrimGroup className={cn(st.Button, st.ZoomButtons)} orientation="vertical">
          <IconButton icon={<PlusIcon />} onClick={() => handleChangeMapView('increase')} />
          <IconButton icon={<MinusIcon />} onClick={() => handleChangeMapView('decrease')} />
        </ButtonScrimGroup>
        <ButtonScrimGroup className={cn(st.Button, st.GeoLocationButton)} orientation="vertical">
          <IconButton icon={<LocationNavigationIcon />} onClick={() => handleChangeMapView('geolocation')} />
        </ButtonScrimGroup>
        <div className={st.Map} id={withPrefixId('map')} ref={mapTargetElement} />
      </div>
    </>
  );

  if (isFullScreen) {
    const root = document.getElementById('root');
    if (root) return createPortal(mapContent, root);
  }

  return (mapContent);
}
